"use client";

import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { 
  MagnifyingGlassIcon, 
  EyeIcon, 
  ShareIcon, 
  ClipboardDocumentIcon,
  AcademicCapIcon,
  MapPinIcon,
  ClockIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { ScheduledCourse } from '@/app/types/course';
import { RutgersCourse } from '@/app/types/RutgersCourse';

interface ContextMenuProps {
  isOpen: boolean;
  x: number;
  y: number;
  course: ScheduledCourse | RutgersCourse | null;
  onClose: () => void;
  onAddToSniper: (course: ScheduledCourse | RutgersCourse) => void;
  onViewInExplore: (course: ScheduledCourse | RutgersCourse) => void;
  onCopyCourseInfo: (course: ScheduledCourse | RutgersCourse) => void;
  onShareCourse: (course: ScheduledCourse | RutgersCourse) => void;
  showSniperOption?: boolean;
}

export default function ContextMenu({
  isOpen,
  x,
  y,
  course,
  onClose,
  onAddToSniper,
  onViewInExplore,
  onCopyCourseInfo,
  onShareCourse,
  showSniperOption = true,
}: ContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [position, setPosition] = useState({ left: x, top: y });

  useLayoutEffect(() => {
    if (!isOpen || !menuRef.current) return;
    const bounds = menuRef.current.getBoundingClientRect();
    const gutter = 8;
    const left = Math.min(Math.max(gutter, x - bounds.width / 2), window.innerWidth - bounds.width - gutter);
    const preferredTop = y - bounds.height - 10;
    const top = preferredTop >= gutter ? preferredTop : Math.min(y + 10, window.innerHeight - bounds.height - gutter);
    setPosition({ left, top: Math.max(gutter, top) });
  }, [isOpen, x, y]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      window.requestAnimationFrame(() => menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus());
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      if (isOpen) previousFocusRef.current?.focus();
    };
  }, [isOpen, onClose]);

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') || []);
    const current = items.indexOf(document.activeElement as HTMLButtonElement);
    let next = current;
    if (event.key === 'ArrowDown') next = (current + 1) % items.length;
    else if (event.key === 'ArrowUp') next = (current - 1 + items.length) % items.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = items.length - 1;
    else if (event.key === 'Tab') { onClose(); return; }
    else return;
    event.preventDefault();
    items[next]?.focus();
  };

  if (!isOpen || !course || typeof document === 'undefined') return null;

  // Extract course information based on type
  const courseName = 'name' in course ? course.name : course.courseName;
  const department = course.department;
  const classNumber = course.class_number;
  const section = course.section;
  const campus = course.campus;
  const credits = course.credits;
  const instructors = course.instructors;
  const meetingTimes = course.meetingTimes;
  const location = 'location' in course ? course.location : meetingTimes?.[0]?.location;
  const coreRequirements = course.coreRequirements;

  const menuItems = [
    ...(showSniperOption ? [{
      icon: <EyeIcon className="h-4 w-4" />,
      label: 'Add to Course Sniper',
      action: () => onAddToSniper(course),
      description: 'Monitor this course for open sections'
    }] : []),
    {
      icon: <MagnifyingGlassIcon className="h-4 w-4" />,
      label: 'View in Explore',
      action: () => onViewInExplore(course),
      description: 'See detailed course information'
    },
    {
      icon: <ClipboardDocumentIcon className="h-4 w-4" />,
      label: 'Copy Course Info',
      action: () => onCopyCourseInfo(course),
      description: 'Copy course details to clipboard'
    },
    {
      icon: <ShareIcon className="h-4 w-4" />,
      label: 'Share Course',
      action: () => onShareCourse(course),
      description: 'Share course with friends'
    },
  ];

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label={`Actions for ${courseName}`}
      onKeyDown={handleMenuKeyDown}
      className="ss-context-menu ss-overlay-panel fixed z-50 min-w-64 overflow-y-auto rounded-control border py-2 shadow-e2"
      data-ss-overlay-material="transient"
      data-ss-overlay-scroll-region
      style={{
        left: position.left,
        top: position.top,
      }}
    >
      {/* Course Info Header */}
      <div className="ss-overlay-divider mb-2 border-b px-4 py-3">
        <div className="break-words text-sm font-semibold text-[var(--ss-text)]">
          {courseName}
        </div>
        <div className="text-xs text-[var(--ss-text-muted)]">
          {department} {classNumber} • Section {section}
        </div>
        <div className="mt-1 text-xs text-[var(--ss-text-muted)]">
          {campus} • {credits} credits
        </div>
      </div>

      {/* Menu Items */}
      <div className="py-1">
        {menuItems.map((item, index) => (
          <button
            key={index}
            role="menuitem"
            onClick={() => {
              item.action();
              onClose();
            }}
            className="group flex min-h-12 w-full items-center space-x-3 px-4 py-2 text-left hover:bg-[var(--ss-surface-subtle)]"
          >
            <div className="text-[var(--ss-text-muted)] group-hover:text-[var(--ss-text)]">
              {item.icon}
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-[var(--ss-text)]">
                {item.label}
              </div>
              <div className="text-xs text-[var(--ss-text-muted)]">
                {item.description}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Quick Info Section */}
      <div className="ss-overlay-divider mt-2 border-t px-4 py-3">
        <div className="mb-2 text-xs text-[var(--ss-text-muted)]">Quick Info:</div>
        <div className="space-y-1 text-xs">
          {instructors && instructors.length > 0 && (
            <div className="flex items-center space-x-2">
              <UserIcon className="h-3 w-3 text-[var(--ss-text-muted)]" />
              <span className="text-[var(--ss-text)]">
                {instructors.join(', ')}
              </span>
            </div>
          )}
          {meetingTimes && meetingTimes.length > 0 && (
            <div className="flex items-center space-x-2">
              <ClockIcon className="h-3 w-3 text-[var(--ss-text-muted)]" />
              <span className="text-[var(--ss-text)]">
                {meetingTimes.map(mt => `${mt.day} ${mt.startMinute ? `${Math.floor(mt.startMinute / 60)}:${String(mt.startMinute % 60).padStart(2, '0')}` : 'TBA'}`).join(', ')}
              </span>
            </div>
          )}
          {location && (
            <div className="flex items-center space-x-2">
              <MapPinIcon className="h-3 w-3 text-[var(--ss-text-muted)]" />
              <span className="text-[var(--ss-text)]">{location}</span>
            </div>
          )}
          {coreRequirements && coreRequirements.length > 0 && (
            <div className="flex items-center space-x-2">
              <AcademicCapIcon className="h-3 w-3 text-[var(--ss-text-muted)]" />
              <span className="text-[var(--ss-text)]">
                Core: {coreRequirements.join(', ')}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
